import { Button, Card } from "@prisma/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronRight, Plus, ShieldCheck, UserRound, X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";

import { Dialog } from "../../components/Dialog";
import { QueryState } from "../../components/QueryState";
import { api, ApiError, call } from "../../lib/api";
import { useChainedSave } from "../../lib/chainedSave";
import type { components } from "../../lib/api-types";
import { formatDate } from "../../lib/format";
import { useSession } from "../../lib/session";
import { AssetDrawer, AssetTile, type AssetCard } from "../assets/AssetViews";
import { RelatedFiles } from "../assets/RelatedFiles";
import { UploadDrawer } from "../assets/UploadDrawer";
import { JOB_ICON } from "./ProductionBoardPage";

type Job = components["schemas"]["JobDetailOut"];
type Shooting = components["schemas"]["ShootingOut"];
type ShootingIn = components["schemas"]["ShootingIn"];
type Person = components["schemas"]["AssigneeOut"];
type ShotIn = NonNullable<ShootingIn["shot_list"]>[number];
const shotsIn = (xs: Shooting["shot_list"]): ShotIn[] => xs.map((x) => ({ ...x, preset: x.preset as ShotIn["preset"] }));

const PHASES = ["PLANNED", "SCHEDULED", "SHOOTING", "ASSETS_UPLOADING", "ASSETS_INGESTED", "READY_FOR_EDIT", "COMPLETED"] as const;
const PHASE_ACTIONS = ["schedule", "startShoot", "completeShoot", "markAssetsIngested", "skipMediaPrep"] as const;
const CONSENT = ["PENDING", "COMPLETED", "NOT_REQUIRED"] as const;
const A = { assign: "assign", reassign: "reassign", cancel: "cancel", edit: "editShooting", upload: "upload", createEdit: "createEditingJob" } as const;
const F = { type: "shoot_type", subject: "subject", location: "location" } as const;

/** Production Job Detail (doc 04 P11) with the Shooting Job (P16) for SHOOTING jobs. */
export function JobDetailPage() {
  const { displayId = "" } = useParams();
  const { t, i18n } = useTranslation();
  const me = useSession();
  const qc = useQueryClient();
  const key = ["job", displayId];
  const q = useQuery({ queryKey: key,
    queryFn: () => call<Job>(api.GET("/app/v1/production/jobs/{display_id}", { params: { path: { display_id: displayId } } })) });
  const refresh = () => { void qc.invalidateQueries({ queryKey: key }); void qc.invalidateQueries({ queryKey: ["jobs"] }); };
  return (
    <QueryState query={q}>
      {(j) => {
        const Icon = JOB_ICON[j.job_type];
        return (
          <div className="space-y-6">
            <nav aria-label={t("requests.breadcrumb")} className="flex items-center gap-1.5 text-[13px] text-ink-2">
              <Link to="/production" className="hover:text-ink">{t("nav.production")}</Link>
              <ChevronRight aria-hidden="true" className="size-3.5" />
              <span className="text-ink">{j.display_id}</span>
            </nav>
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  {Icon && <Icon aria-hidden="true" className="size-7 text-violet" />}
                  <h1 className="text-[34px] font-semibold tracking-[-0.02em] text-ink">{j.display_id}</h1>
                  <span className="rounded-full bg-[var(--accent-primary-soft)] px-3 py-1 text-[12px] font-semibold uppercase text-violet-text">{t(`status.production_job.${j.status.toLowerCase()}`)}</span>
                </div>
                <p className="mt-1 text-[15px] text-ink-2">
                  {t(`plan.job_type_${j.job_type.toLowerCase()}`)}{j.shooting?.shoot_type ? ` · ${j.shooting.shoot_type}` : ""} · {j.product.name}
                  {j.campaign_id ? ` · ${j.campaign_id}` : ""} · <Link className="text-violet-text underline" to={`/projects/${j.request}`}>{j.request}</Link>
                </p>
                <p className="mt-1 flex items-center gap-1.5 text-[14px] text-ink-2">
                  <UserRound aria-hidden="true" className="size-4" />{j.assignee?.name ?? t("requests.unassigned")}
                  {j.deadline && ` · ${t("requests.due_on", { date: formatDate(j.deadline, i18n.language, me.time_zone) })}`}
                </p>
              </div>
              <HeaderActions j={j} onDone={refresh} />
            </div>
            {j.cancel_reason && <Card className="border-[var(--status-danger)] p-4 text-[14px] text-ink">{t("production.cancelled_because", { reason: j.cancel_reason })}</Card>}
            {j.shooting && <PhaseTrack j={j} s={j.shooting} onDone={refresh} />}
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
              <div className="min-w-0 space-y-6">
                {j.shooting && <ShootingForm key={j.id} j={j} s={j.shooting} onDone={refresh} />}
                <JobAssets j={j} />
              </div>
              <aside className="space-y-6">
                <Brief j={j} />
                <RelatedFiles entity={j.display_id} canUpload={j.allowed_actions.includes(A.upload)} meta={uploadMeta(t, j, me.name, i18n.language, me.time_zone)} />
              </aside>
            </div>
          </div>
        );
      }}
    </QueryState>
  );
}

function ErrorLine({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  const code = error instanceof ApiError ? error.code : "unknown";
  const rule = error instanceof ApiError ? (error.details.fields as { rule?: string }[] | undefined)?.[0]?.rule : undefined;
  return <p role="alert" className="text-[14px] text-[var(--status-danger)]">{rule ? t(`production.rule_${rule}`, { defaultValue: t(`error.${code}`) }) : t(`error.${code}`, { defaultValue: t("error.unknown") })}</p>;
}

function HeaderActions({ j, onDone }: { j: Job; onDone: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<null | "assign" | "cancel">(null);
  const [person, setPerson] = useState("");
  const [reason, setReason] = useState("");
  const people = useQuery({ queryKey: ["assignees", j.job_type], enabled: open === "assign",
    queryFn: () => call<Person[]>(api.GET("/app/v1/production/assignees", { params: { query: { job_type: j.job_type as "SHOOTING" } } })) });
  const assign = useMutation({
    mutationFn: () => call(api.POST("/app/v1/production/jobs/{display_id}/assign", { params: { path: { display_id: j.display_id } }, body: { assignee_id: person, row_version: j.row_version } })),
    onSuccess: () => { setOpen(null); onDone(); },
  });
  const cancel = useMutation({
    mutationFn: () => call(api.POST("/app/v1/production/jobs/{display_id}/cancel", { params: { path: { display_id: j.display_id } }, body: { reason: reason.trim(), row_version: j.row_version } })),
    onSuccess: () => { setOpen(null); onDone(); },
  });
  const can = (a: string) => j.allowed_actions.includes(a);
  return (
    <div className="flex flex-wrap gap-2">
      {j.job_type === "EDITING" && (
        <Link to={`/editor/jobs/${j.display_id}`} className="inline-flex h-10 items-center gap-2 rounded-[var(--radius-control)] border border-line bg-surface px-4 text-sm font-medium text-ink hover:bg-elevated">{t("editor.open_editing_job")}</Link>
      )}
      {can(A.createEdit) && <CreateEditingJob j={j} onDone={onDone} />}
      {can(A.cancel) && <Button variant="ghost" onClick={() => setOpen("cancel")}>{t("production.cancel_job")}</Button>}
      {(can(A.assign) || can(A.reassign)) && (
        <Button variant="primary" onClick={() => setOpen("assign")}>{t(can(A.assign) ? "production.assign" : "production.reassign")}</Button>
      )}
      <Dialog open={open === "assign"} onOpenChange={(o) => !o && setOpen(null)} title={t("production.assign_title")} description={t(`production.assign_body_${j.job_type.toLowerCase()}`, { defaultValue: "" })}>
        <label htmlFor="assignee" className="mb-1 block text-[13px] font-medium text-ink-2">{t("production.assignee")}</label>
        <select id="assignee" value={person} onChange={(e) => setPerson(e.target.value)} className="h-10 w-full rounded-[var(--radius-control)] border border-line bg-surface px-3 text-[14px] text-ink">
          <option value="">{t("production.choose_person")}</option>
          {(people.data ?? []).filter((p) => p.id !== j.assignee?.id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <ErrorLine error={assign.error} />
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setOpen(null)}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={!person || assign.isPending} onClick={() => assign.mutate()}>{t("production.assign")}</Button>
        </div>
      </Dialog>
      <Dialog open={open === "cancel"} onOpenChange={(o) => !o && setOpen(null)} title={t("production.cancel_title")} description={t("production.cancel_body")}>
        <label htmlFor="cancel-reason" className="mb-1 block text-[13px] font-medium text-ink-2">{t("production.cancel_reason")}</label>
        <textarea id="cancel-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="w-full rounded-[var(--radius-control)] border border-line bg-surface p-3 text-[14px] text-ink" />
        <ErrorLine error={cancel.error} />
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setOpen(null)}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={!reason.trim() || cancel.isPending} onClick={() => cancel.mutate()}>{t("production.cancel_job")}</Button>
        </div>
      </Dialog>
    </div>
  );
}

function PhaseTrack({ j, s, onDone }: { j: Job; s: Shooting; onDone: () => void }) {
  const { t } = useTranslation();
  const act = useMutation({
    mutationFn: (action: (typeof PHASE_ACTIONS)[number]) => call(api.POST("/app/v1/production/jobs/{display_id}/shooting/{action}", {
      params: { path: { display_id: j.display_id, action } }, body: { row_version: j.row_version } })),
    onSuccess: onDone,
  });
  const current = PHASES.indexOf(s.phase as (typeof PHASES)[number]);
  const next = PHASE_ACTIONS.filter((a) => j.allowed_actions.includes(a));
  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <ol aria-label={t("production.phases")} className="flex flex-wrap items-center gap-2">
          {PHASES.map((p, i) => (
            <li key={p} aria-current={i === current ? "step" : undefined}
              className={`rounded-full px-3 py-1 text-[12px] font-medium ${i === current ? "bg-[var(--accent-primary-soft)] text-violet-text" : i < current ? "text-[var(--status-success)]" : "text-ink-2"}`}>
              {i < current && <Check aria-hidden="true" className="mr-1 inline size-3" />}{t(`production.phase_${p.toLowerCase()}`)}
            </li>
          ))}
        </ol>
        <div className="flex gap-2">
          {next.map((a) => <Button key={a} variant="primary" disabled={act.isPending} onClick={() => act.mutate(a)}>{t(`production.action_${a}`)}</Button>)}
          {s.phase === "ASSETS_UPLOADING" && <span className="self-center text-[13px] text-ink-2">{t("production.upload_then_ingest")}</span>}
        </div>
      </div>
      <ErrorLine error={act.error} />
    </Card>
  );
}

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1 block text-[13px] font-medium text-ink-2">{label}</label>
      {children}
    </div>
  );
}

function ShootingForm({ j, s, onDone }: { j: Job; s: Shooting; onDone: () => void }) {
  const { t } = useTranslation();
  const editable = j.allowed_actions.includes(A.edit);
  const [v, setV] = useState({ ...s, shoot_date: s.shoot_date ? s.shoot_date.slice(0, 16) : "" });
  const [newShot, setNewShot] = useState("");
  const [newCrew, setNewCrew] = useState({ name: "", role: "" });
  const [newQ, setNewQ] = useState("");
  const save = useChainedSave<Partial<ShootingIn>>(s.row_version, (changes, row_version) =>
    call<{ row_version: number }>(api.PATCH("/app/v1/production/jobs/{display_id}/shooting", {
      params: { path: { display_id: j.display_id } }, body: { ...changes, row_version } })), onDone);
  const error = save.error;
  const input = "w-full rounded-[var(--radius-control)] border border-line bg-surface px-3 py-2 text-[14px] text-ink disabled:bg-elevated";
  const text = (k: "shoot_type" | "subject" | "location") => (
    <Field label={t(`production.field_${k}`)} htmlFor={`sj-${k}`}>
      <input id={`sj-${k}`} className={input} disabled={!editable} value={v[k] ?? ""} maxLength={300}
        onChange={(e) => setV({ ...v, [k]: e.target.value })} onBlur={(e) => e.target.value !== (s[k] ?? "") && save.mutate({ [k]: e.target.value || null })} />
    </Field>
  );
  // Local copies so ticks and choices show at once; the card remounts on the next row_version.
  const [shots, setShots] = useState(s.shot_list);
  const [consent, setConsent] = useState(s.consent_status);
  const saveShots = (next: Shooting["shot_list"]) => { setShots(next); save.mutate({ shot_list: shotsIn(next) }); };
  return (
    <Card className="p-6">
      <h2 className="text-[18px] font-semibold text-ink">{t("production.shooting_job")}</h2>
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        {text(F.type)}
        {text(F.subject)}
        {text(F.location)}
        <Field label={t("production.field_shoot_date")} htmlFor="sj-date">
          <input id="sj-date" type="datetime-local" className={input} disabled={!editable} value={v.shoot_date}
            onChange={(e) => setV({ ...v, shoot_date: e.target.value })}
            onBlur={(e) => save.mutate({ shoot_date: e.target.value ? new Date(e.target.value).toISOString() : null })} />
        </Field>
      </div>

      <h3 className="mt-6 text-[15px] font-semibold text-ink">{t("production.crew")}</h3>
      <ul className="mt-2 flex flex-wrap gap-2">
        {s.crew.map((c, n) => (
          <li key={n} className="inline-flex items-center gap-1.5 rounded-full border border-line px-3 py-1 text-[13px] text-ink">
            {c.name}{c.role ? ` · ${c.role}` : ""}
            {editable && <button type="button" aria-label={t("karya.remove_item")} onClick={() => save.mutate({ crew: s.crew.filter((_, i) => i !== n).map((x) => ({ name: x.name, role: x.role ?? null })) })}><X aria-hidden="true" className="size-3.5 text-ink-2" /></button>}
          </li>
        ))}
        {s.crew.length === 0 && <li className="text-[13px] text-ink-2">{t("production.crew_empty")}</li>}
      </ul>
      {editable && (
        <form className="mt-2 flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); if (newCrew.name.trim()) { save.mutate({ crew: [...s.crew.map((x) => ({ name: x.name, role: x.role ?? null })), { name: newCrew.name.trim(), role: newCrew.role.trim() || null }] }); setNewCrew({ name: "", role: "" }); } }}>
          <label className="sr-only" htmlFor="crew-name">{t("production.crew_name")}</label>
          <input id="crew-name" className={`${input} w-48`} placeholder={t("production.crew_name")} value={newCrew.name} onChange={(e) => setNewCrew({ ...newCrew, name: e.target.value })} />
          <label className="sr-only" htmlFor="crew-role">{t("production.crew_role")}</label>
          <input id="crew-role" className={`${input} w-40`} placeholder={t("production.crew_role")} value={newCrew.role} onChange={(e) => setNewCrew({ ...newCrew, role: e.target.value })} />
          <Button type="submit" variant="secondary" disabled={!newCrew.name.trim()}><Plus aria-hidden="true" className="size-4" />{t("production.add_crew")}</Button>
        </form>
      )}

      <h3 className="mt-6 text-[15px] font-semibold text-ink">{t("production.shot_list")}</h3>
      <ul className="mt-2 space-y-1.5">
        {shots.map((x) => (
          <li key={x.id} className="flex items-center gap-2 text-[14px] text-ink">
            <label className="flex flex-1 items-center gap-2">
              <input type="checkbox" checked={x.done} disabled={!editable}
                onChange={() => saveShots(shots.map((y) => (y.id === x.id ? { ...y, done: !y.done } : y)))} />
              <span className={x.done ? "text-ink-2 line-through" : ""}>{x.preset ? t(`production.shot_${x.preset.toLowerCase()}`) : x.label}</span>
            </label>
            {editable && <button type="button" aria-label={t("karya.remove_item")} onClick={() => saveShots(shots.filter((y) => y.id !== x.id))}><X aria-hidden="true" className="size-3.5 text-ink-2" /></button>}
          </li>
        ))}
      </ul>
      {editable && (
        <form className="mt-2 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (newShot.trim()) { saveShots([...shots, { id: `c${Date.now()}`, preset: null, label: newShot.trim(), done: false }]); setNewShot(""); } }}>
          <label className="sr-only" htmlFor="new-shot">{t("production.add_shot")}</label>
          <input id="new-shot" className={input} placeholder={t("production.add_shot")} value={newShot} maxLength={300} onChange={(e) => setNewShot(e.target.value)} />
          <Button type="submit" variant="secondary" aria-label={t("production.add_shot")} disabled={!newShot.trim()}><Plus aria-hidden="true" className="size-4" /></Button>
        </form>
      )}

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label={t("production.field_script")} htmlFor="sj-script">
          <textarea id="sj-script" rows={5} className={input} disabled={!editable} value={v.script ?? ""} maxLength={4000}
            onChange={(e) => setV({ ...v, script: e.target.value })} onBlur={(e) => e.target.value !== (s.script ?? "") && save.mutate({ script: e.target.value || null })} />
        </Field>
        <div>
          <div className="mb-1 text-[13px] font-medium text-ink-2">{t("production.interview_questions")}</div>
          <ol className="list-decimal space-y-1 pl-5 text-[14px] text-ink">
            {s.interview_questions.map((qq, n) => (
              <li key={n}><span className="inline-flex items-center gap-2">{qq}{editable && <button type="button" aria-label={t("karya.remove_item")} onClick={() => save.mutate({ interview_questions: s.interview_questions.filter((_, i) => i !== n) })}><X aria-hidden="true" className="size-3.5 text-ink-2" /></button>}</span></li>
            ))}
          </ol>
          {editable && (
            <form className="mt-2 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (newQ.trim()) { save.mutate({ interview_questions: [...s.interview_questions, newQ.trim()] }); setNewQ(""); } }}>
              <label className="sr-only" htmlFor="new-q">{t("production.add_question")}</label>
              <input id="new-q" className={input} placeholder={t("production.add_question")} value={newQ} maxLength={300} onChange={(e) => setNewQ(e.target.value)} />
              <Button type="submit" variant="secondary" aria-label={t("production.add_question")} disabled={!newQ.trim()}><Plus aria-hidden="true" className="size-4" /></Button>
            </form>
          )}
        </div>
      </div>

      <fieldset className="mt-6">
        <legend className="flex items-center gap-2 text-[15px] font-semibold text-ink"><ShieldCheck aria-hidden="true" className="size-4 text-violet" />{t("production.consent")}</legend>
        <div className="mt-2 flex flex-wrap gap-2">
          {CONSENT.map((c) => (
            <label key={c} className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-[13px] ${consent === c ? "border-[var(--accent-primary)] text-violet-text" : "border-line text-ink"}`}>
              <input type="radio" name="consent" checked={consent === c} disabled={!editable} onChange={() => { setConsent(c); save.mutate({ consent_status: c }); }} />
              {t(`production.consent_option_${c.toLowerCase()}`)}
            </label>
          ))}
        </div>
        <p className="mt-2 text-[12px] text-ink-2">{t("production.consent_note_hint")}</p>
      </fieldset>
      <div className="mt-3"><ErrorLine error={error} /></div>
    </Card>
  );
}

function Brief({ j }: { j: Job }) {
  const { t } = useTranslation();
  const row = (label: string, value: ReactNode) => (
    <div className="flex gap-3 py-2"><dt className="w-32 shrink-0 text-ink-2">{label}</dt><dd className="min-w-0 text-ink">{value ?? <span className="text-ink-2">{t("requests.not_provided")}</span>}</dd></div>
  );
  return (
    <Card className="p-5">
      <h2 className="text-[16px] font-semibold text-ink">{t("production.brief")}</h2>
      <dl className="mt-2 divide-y divide-[var(--border-default)] text-[13px]">
        {row(t("requests.col_product"), j.product.name)}
        {row(t("requests.campaign"), j.campaign_id)}
        {row(t("karya.label_hypothesis"), j.hypothesis?.name)}
        {row(t("karya.hyp_angle"), j.hypothesis?.angle)}
        {row(t("karya.hyp_hook_direction"), j.hypothesis?.hook_direction)}
        {row(t("plan.field_audience_text"), j.plan?.audience_text)}
        {row(t("plan.field_cta"), j.plan?.cta)}
        {j.plan_item && row(t("plan.deliverables"), `${t(`requests.deliverable_n_${j.plan_item.deliverable_type.toLowerCase()}`, { count: j.plan_item.quantity })}${j.plan_item.preferred_format ? ` · ${j.plan_item.preferred_format}` : ""}`)}
        {j.plan_item && j.plan_item.distribution_targets.length > 0 && row(t("plan.distribution"), j.plan_item.distribution_targets.map((x) => t(`plan.target_${x.toLowerCase()}`)).join(", "))}
      </dl>
      <Link to={`/projects/${j.request}/plan`} className="mt-3 inline-block text-[13px] text-violet-text hover:underline">{t("production.open_plan", { n: j.plan?.version ?? 1 })}</Link>
    </Card>
  );
}

function uploadMeta(t: (k: string) => string, j: Job, uploader: string, lng: string, tz: string) {
  return [
    { label: t("assets.meta_job"), value: j.display_id },
    { label: t("requests.col_product"), value: j.product.name },
    { label: t("requests.campaign"), value: j.campaign_id ?? "—" },
    { label: t("assets.meta_date"), value: formatDate(new Date().toISOString(), lng, tz) },
    { label: t("assets.meta_uploader"), value: uploader },
  ];
}

function JobAssets({ j }: { j: Job }) {
  const { t, i18n } = useTranslation();
  const me = useSession();
  const [upload, setUpload] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const key = ["job", j.display_id, "assets"];
  const q = useQuery({ queryKey: key,
    queryFn: () => call<AssetCard[]>(api.GET("/app/v1/production/jobs/{display_id}/assets", { params: { path: { display_id: j.display_id } } })) });
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[18px] font-semibold text-ink">{t("production.assets")}</h2>
        {j.allowed_actions.includes(A.upload) && <Button variant="primary" onClick={() => setUpload(true)}>{t("assets.upload_assets")}</Button>}
      </div>
      {(q.data ?? []).length === 0 ? (
        <p className="mt-2 text-[14px] text-ink-2">{t("assets.job_empty")}</p>
      ) : (
        <ul className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
          {(q.data ?? []).map((a) => <AssetTile key={a.id} a={a} onOpen={setOpen} />)}
        </ul>
      )}
      <UploadDrawer open={upload} onClose={() => setUpload(false)} target={{ target_kind: "production_job", target_id: j.display_id }}
        meta={uploadMeta(t, j, me.name, i18n.language, me.time_zone)} invalidate={[key, ["job", j.display_id], ["assets"]]} />
      <AssetDrawer id={open} onClose={() => setOpen(null)} onOpen={setOpen} />
    </Card>
  );
}

function CreateEditingJob({ j, onDone }: { j: Job; onDone: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [v, setV] = useState({ target_duration_s: "", platform: "", brief_notes: "" });
  const m = useMutation({
    mutationFn: () => call<{ display_id: string }>(api.POST("/app/v1/production/jobs/{display_id}/editing-job", {
      params: { path: { display_id: j.display_id } },
      body: { target_duration_s: v.target_duration_s ? Number(v.target_duration_s) : null, platform: v.platform.trim() || null, brief_notes: v.brief_notes.trim() || null, accept_shoot: true } })),
    onSuccess: (r) => { setOpen(false); onDone(); void navigate(`/production/jobs/${r.display_id}`); },
  });
  const input = "h-10 w-full rounded-[var(--radius-control)] border border-line bg-surface px-3 text-[14px] text-ink";
  return (
    <>
      <Button variant="primary" onClick={() => setOpen(true)}>{t("editor.create_editing_job")}</Button>
      <Dialog open={open} onOpenChange={setOpen} title={t("editor.create_title")} description={t("editor.create_body")}>
        <label htmlFor="ce-duration" className="mb-1 block text-[13px] font-medium text-ink-2">{t("editor.field_target_duration_s")}</label>
        <input id="ce-duration" type="number" min={1} max={3600} className={input} value={v.target_duration_s} onChange={(e) => setV({ ...v, target_duration_s: e.target.value })} />
        <label htmlFor="ce-platform" className="mb-1 mt-3 block text-[13px] font-medium text-ink-2">{t("editor.field_platform")}</label>
        <input id="ce-platform" className={input} value={v.platform} onChange={(e) => setV({ ...v, platform: e.target.value })} />
        <label htmlFor="ce-notes" className="mb-1 mt-3 block text-[13px] font-medium text-ink-2">{t("editor.field_brief_notes")}</label>
        <textarea id="ce-notes" rows={3} className="w-full rounded-[var(--radius-control)] border border-line bg-surface p-3 text-[14px] text-ink" value={v.brief_notes} onChange={(e) => setV({ ...v, brief_notes: e.target.value })} />
        <ErrorLine error={m.error} />
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={m.isPending} onClick={() => m.mutate()}>{t("editor.create_editing_job")}</Button>
        </div>
      </Dialog>
    </>
  );
}
